<template>
  <div class="ab-test-waterfall">
    <!-- Loading State -->
    <div
      v-if="isSubmitting"
      class="d-flex flex-column align-center py-8"
    >
      <h3 class="mb-4">
        Submitting {{ populationCount }} Leads...
      </h3>
      <v-progress-circular
        :size="50"
        color="primary"
        indeterminate
      />
      <div class="mt-2 text-caption">
        This may take a few moments
      </div>
    </div>

    <!-- Main Content -->
    <div v-else>
      <!-- Configuration -->
      <v-card
        class="mb-4"
        tile
        outlined
      >
        <v-card-title class="pb-2">
          <v-icon class="mr-2">
            mdi-cog
          </v-icon>
          Configuration
        </v-card-title>
        <v-card-text>
          <v-row>
            <v-col
              cols="12"
              md="8"
            >
              <v-select
                v-model.number="populationCount"
                :items="populationOptions"
                label="Population Count"
                :disabled="isSubmitting"
                prepend-icon="mdi-account-group"
              />
            </v-col>
            <v-col
              cols="12"
              md="4"
              class="d-flex align-center"
            >
              <v-btn
                color="primary"
                :loading="isSubmitting"
                :disabled="!hasLeadData"
                @click="submitLeads"
              >
                <v-icon
                  left
                  small
                >
                  mdi-play
                </v-icon>
                Submit Leads
              </v-btn>
              <v-btn
                v-if="hasResults"
                text
                color="secondary"
                class="ml-2"
                @click="clearResults"
              >
                <v-icon
                  left
                  small
                >
                  mdi-refresh
                </v-icon>
                Clear
              </v-btn>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- Error Alert -->
      <v-alert
        v-if="errorMessage"
        type="error"
        dismissible
        dense
        class="mb-4"
        @input="errorMessage = ''"
      >
        {{ errorMessage }}
      </v-alert>

      <!-- Leads Report -->
      <v-card
        v-if="leadsReport"
        class="mb-4"
        outlined
        tile
      >
        <v-card-title class="pb-2">
          <v-icon class="mr-2">
            mdi-chart-line
          </v-icon>
          API Calls Report
          <v-spacer />
          <v-chip
            :color="getSuccessRateColor(leadsReport.successRate)"
            outlined
          >
            {{ leadsReport.successRate }}% Success
          </v-chip>
        </v-card-title>
        <v-card-text>
          <v-row>
            <v-col
              v-for="(metric, index) in reportMetrics"
              :key="index"
              cols="6"
              sm="3"
            >
              <div class="text-center pa-2">
                <v-icon
                  :color="metric.iconColor"
                  size="24"
                  class="mb-1"
                >
                  {{ metric.icon }}
                </v-icon>
                <div class="text-h6 font-weight-bold">
                  {{ metric.value }}
                </div>
                <div class="text-caption">
                  {{ metric.label }}
                </div>
              </div>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- A/B Test Results -->
      <div v-if="leadsReportBreakdowns.length">
        <div>
          <h2 class="mb-3">
            Leads Distribution
          </h2>

          <v-spacer />
        </div>

        <v-card
          v-for="result in leadsReportBreakdowns"
          :key="result.abTestName"
          class="mb-3"
          tile
          outlined
        >
          <v-card-title class="pb-2 mb-4">
            <v-icon
              class="mr-2"
              color="primary"
              size="20"
            >
              mdi-test-tube
            </v-icon>
            {{ result.abTestName }}
            <v-spacer />
            <v-chip
              color="primary"
              outlined
            >
              {{ result.leadCount }} leads ({{ result.percentage }}%)
            </v-chip>
          </v-card-title>

          <v-card-text>
            <!-- Strategy Cards -->
            <v-row dense>
              <v-col
                v-for="strategy in result.strategies"
                :key="strategy.strategyId"
                cols="12"
                sm="6"
                md="4"
              >
                <v-card
                  outlined
                  tile
                  class="pa-2"
                >
                  <div class="d-flex justify-space-between align-center mb-1">
                    <span class="text-body-2 font-weight-medium">
                      {{ strategy.strategyName }}
                    </span>
                    <v-chip
                      :color="getStrategyColor(strategy.percentage)"
                      dark
                    >
                      {{ strategy.percentage }}%
                    </v-chip>
                  </div>
                  <div class="text-subtitle-2 mb-1">
                    {{ strategy.leadCount }} leads
                  </div>
                  <v-progress-linear
                    :value="parseFloat(strategy.percentage)"
                    :color="getStrategyColor(strategy.percentage)"
                    height="4"
                    rounded
                  />
                </v-card>
              </v-col>
            </v-row>

            <!-- Detailed Table (Collapsible) -->
            <v-expansion-panels
              accordion
              tile
              class="mt-3"
            >
              <v-expansion-panel>
                <v-expansion-panel-header class="py-2">
                  <span class="text-body-2">
                    <v-icon
                      left
                      small
                    >mdi-table</v-icon>
                    Detailed Breakdown
                  </span>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-simple-table dense>
                    <thead>
                      <tr>
                        <th>Strategy</th>
                        <th class="text-center">
                          Count
                        </th>
                        <th class="text-center">
                          %
                        </th>
                        <th class="text-center">
                          Distribution
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="strategy in result.strategies"
                        :key="strategy.strategyId"
                      >
                        <td class="text-body-2">
                          {{ strategy.strategyName }}
                        </td>
                        <td class="text-center">
                          {{ strategy.leadCount }}
                        </td>
                        <td class="text-center">
                          {{ strategy.percentage }}%
                        </td>
                        <td class="text-center">
                          <v-progress-linear
                            :value="parseFloat(strategy.percentage)"
                            :color="getStrategyColor(strategy.percentage)"
                            height="3"
                            rounded
                            style="min-width: 60px"
                          />
                        </td>
                      </tr>
                    </tbody>
                  </v-simple-table>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </v-card-text>
        </v-card>
      </div>

      <!-- Empty State -->
      <v-card
        v-else-if="!leadsReport"
        class="text-center pa-6"

        tile
        outlined
      >
        <v-icon
          size="48"
          color="grey lighten-1"
          class="mb-3"
        >
          mdi-chart-box-outline
        </v-icon>
        <h4 class="grey--text mb-1">
          No Results
        </h4>
        <p class="grey--text text-body-2">
          Submit some leads to see results
        </p>
      </v-card>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import { faker } from '@faker-js/faker';

export default {
  name: 'AbWaterfallTestResult',

  props: {
    leadData: { type: Object, default: () => ({}) },
  },

  emits: ['submitting'],

  data: () => ({
    isSubmitting: false,
    errorMessage: '',

    populationCount: 10,

    leadsReport: null,
    leadsReportBreakdowns: [],
  }),

  computed: {
    populationOptions() {
      const options = [
        1, 5, 10, 25, 50, 75, 100,
        ...Array.from({ length: 9 }, (_, i) => (i + 2) * 100), // 200–1000
      ];
      return options.map((value) => ({ value, text: `${value.toLocaleString()} leads` }));
    },

    hasLeadData() {
      return this.leadData && Object.keys(this.leadData).length > 0;
    },

    hasResults() {
      return this.leadsReportBreakdowns.length > 0 || this.leadsReport;
    },

    reportMetrics() {
      if (!this.leadsReport) return [];

      const {
        total, successful, failed, successRate,
      } = this.leadsReport;
      return [
        {
          label: 'Total',
          value: total.toLocaleString(),
          icon: 'mdi-send',
          iconColor: 'primary',
        },
        {
          label: 'Success',
          value: successful.toLocaleString(),
          icon: 'mdi-check-circle',
          iconColor: 'success',
        },
        {
          label: 'Failed',
          value: failed.toLocaleString(),
          icon: 'mdi-alert-circle',
          iconColor: 'error',
        },
        {
          label: 'Rate',
          value: `${successRate}%`,
          icon: 'mdi-chart-line',
          iconColor: this.getSuccessRateColor(successRate),
        },
      ];
    },
  },

  watch: {
    isSubmitting(val) {
      this.$emit('submitting', val);
    },
  },

  methods: {
    ...mapActions('notification', ['setNotification']),

    async submitLeads() {
      this.isSubmitting = true;
      this.errorMessage = '';

      try {
        const requests = [];
        Array.from({ length: this.populationCount }).forEach(() => {
          let payload = Object.assign(this.leadData, {});

          if (!this.leadData.return_customer) {
            payload = {
              ...payload,
              firstname: faker.person.firstName(),
              lastname: faker.person.lastName(),
              ssn: faker.random.numeric(9),
              email: `test-${faker.random.numeric(9)}@user.com`,
              city: faker.location.city(),
              accountnumber: faker.finance.accountNumber(),
              ipaddress: faker.internet.ipv4(),
            };
          }

          const request = this.$axios
            .post('api/v1/ab-tests/waterfall-test', { ...payload })
            .catch((err) => err.response || { data: null, status: err.status || 500 });

          requests.push(request);
        });

        const results = await Promise.allSettled(requests);

        const successful = results.filter(
          (r) => r.status === 'fulfilled' && r.value?.data && r.value?.status >= 200 && r.value?.status < 300,
        ).length;

        const apiResponses = {
          total: this.populationCount,
          successful,
          failed: results.length - successful,
          results: results.map((r, i) => ({
            request: i + 1,
            status: r.status,
            data: r.value?.data,
            httpStatus: r.value?.status,
          })),
        };

        this.leadsReport = {
          ...apiResponses,
          successRate: this.calculatePercentage(successful, this.populationCount),
        };

        this.leadsReportBreakdowns = this.summarizeAbTest(apiResponses);

        this.setNotification({
          message: `Waterfall completed: ${successful}/${this.populationCount} successful`,
          color: successful === this.populationCount ? 'success' : 'warning',
        });
      } catch (error) {
        this.errorMessage = 'Waterfall failed. Please try again.';
        this.setNotification({ message: 'Waterfall failed', color: 'error' });
      } finally {
        this.isSubmitting = false;
      }
    },

    clearResults() {
      this.leadsReportBreakdowns = [];
      this.leadsReport = null;
      this.errorMessage = '';
    },

    getSuccessRateColor(rate) {
      const num = parseFloat(rate);
      // eslint-disable-next-line no-nested-ternary
      return num >= 90 ? 'success' : num >= 70 ? 'warning' : 'error';
    },

    getStrategyColor(percentage) {
      const num = parseFloat(percentage);
      // eslint-disable-next-line no-nested-ternary
      return num >= 40 ? 'success' : num >= 20 ? 'warning' : 'info';
    },

    summarizeAbTest(response) {
      const leadData = response.results.map((r) => r.data).filter((d) => d?.strategyId);
      if (!leadData.length) return [];

      const totalLeads = leadData.length;
      const abTests = leadData.reduce((acc, lead) => {
        const key = lead.abTestName ? `A/B Test ${lead.abTestName}` : 'Default Router';
        if (!acc[key]) acc[key] = { abTestName: key, leads: [], leadCount: 0 };
        acc[key].leads.push(lead);
        // eslint-disable-next-line no-plusplus
        acc[key].leadCount++;
        return acc;
      }, {});

      return Object.values(abTests).map(({ abTestName, leads, leadCount }) => ({
        abTestName,
        leadCount,
        percentage: this.calculatePercentage(leadCount, totalLeads),
        strategies: this.summarizeStrategyUsage(leads),
      }));
    },

    summarizeStrategyUsage(leads) {
      const total = leads.length;
      const strategies = leads.reduce((acc, lead) => {
        const { strategyId, strategyName } = lead;
        if (!acc[strategyId]) acc[strategyId] = { strategyId, strategyName, leadCount: 0 };
        // eslint-disable-next-line no-plusplus
        acc[strategyId].leadCount++;
        return acc;
      }, {});

      return Object.values(strategies)
        .map(({ leadCount, ...rest }) => ({
          ...rest,
          leadCount,
          percentage: this.calculatePercentage(leadCount, total),
        }))
        .sort((a, b) => b.leadCount - a.leadCount);
    },

    calculatePercentage(part, total) {
      return total > 0 ? ((part / total) * 100).toFixed(2) : '0.00';
    },
  },
};
</script>

<style scoped>
.ab-test-waterfall {
  max-width: 100%;
}

.v-card {
  border-radius: 8px !important;
}

.v-progress-linear {
  border-radius: 2px !important;
}
</style>
